/**
 * 문서 기반 동기화 백엔드 — 결정 #20. `SyncBackend`의 구체 구현으로, 레코드와
 * 에셋 blob을 **단일 직렬화 가능한 문서**(`SyncDocument`)에 담는다.
 *
 * 이 백엔드는 두 용도의 토대다:
 *  1) 파일 동기화(OAuth 불필요): 문서를 JSON 파일로 내보내고 다른 기기에서 불러와
 *     `runSync`로 3-way 병합한다(백업과 달리 침묵의 손실 없이 합친다, 결정 #20).
 *  2) 구글 드라이브 동기화(#8): 드라이브 어댑터가 이 문서를 appDataFolder 파일의
 *     본문으로 쓰고, pull/push를 네트워크에 매핑한다(전송만 교체).
 *
 * 플랫폼 중립(결정 #10): DOM/Node 전용 API 없이 표준 `Blob`/`atob`/`btoa`만 쓴다
 * (브라우저·Node 20+·RN(polyfill) 공통).
 */
import type {
  SyncBackend,
  SyncManifest,
  SyncRecord,
} from "../ports/sync.js";

/** 직렬화 가능한 동기화 문서(파일 본문 / 드라이브 파일 본문). */
export interface SyncDocument {
  format: "roqeet-sync";
  version: 1;
  /** id → 레코드(엔티티 스냅샷 + sync 메타). */
  records: Record<string, SyncRecord>;
  /** 콘텐츠 주소화 에셋 바이트(hash → base64), 결정 #18. */
  blobs: Record<string, { mime: string; base64: string }>;
}

/** 빈 동기화 문서. */
export function emptySyncDocument(): SyncDocument {
  return { format: "roqeet-sync", version: 1, records: {}, blobs: {} };
}

/** 임의 값이 유효한 SyncDocument인지(파일 임포트 검증). */
export function isSyncDocument(value: unknown): value is SyncDocument {
  if (!value || typeof value !== "object") return false;
  const d = value as Partial<SyncDocument>;
  return d.format === "roqeet-sync" && d.version === 1 && !!d.records && !!d.blobs;
}

const SUBTLE_BTOA: (s: string) => string = btoa;
const SUBTLE_ATOB: (s: string) => string = atob;

/** Blob → base64(교환용, 결정 #18). */
async function blobToBase64(blob: Blob): Promise<string> {
  const buf = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < buf.length; i++) binary += String.fromCharCode(buf[i]!);
  return SUBTLE_BTOA(binary);
}

/** base64 → Blob. */
function base64ToBlob(base64: string, mime: string): Blob {
  const binary = SUBTLE_ATOB(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

/**
 * 문서 기반 백엔드. 인메모리 문서를 들고 `SyncBackend`를 구현한다. {@link toDocument}
 * /{@link fromDocument}로 직렬화해 파일·드라이브에 싣는다.
 */
export class DocumentSyncBackend implements SyncBackend {
  readonly kind = "file";
  private records: Map<string, SyncRecord>;
  private blobs: Map<string, { mime: string; base64: string }>;

  constructor(doc: SyncDocument = emptySyncDocument()) {
    this.records = new Map(Object.entries(doc.records));
    this.blobs = new Map(Object.entries(doc.blobs));
  }

  /** 현재 상태를 직렬화 가능한 문서로 내보낸다(파일 저장/드라이브 업로드용). */
  toDocument(): SyncDocument {
    return {
      format: "roqeet-sync",
      version: 1,
      records: Object.fromEntries(this.records),
      blobs: Object.fromEntries(this.blobs),
    };
  }

  async pullManifest(): Promise<SyncManifest> {
    const m: SyncManifest = {};
    for (const [id, r] of this.records) {
      m[id] = { hash: r.hash, rev: r.rev, ...(r.deleted ? { deleted: true } : {}) };
    }
    return m;
  }

  async pullRecords(ids: string[]): Promise<SyncRecord[]> {
    const out: SyncRecord[] = [];
    for (const id of ids) {
      const r = this.records.get(id);
      if (r) out.push(r);
    }
    return out;
  }

  async pushRecords(records: SyncRecord[]): Promise<void> {
    for (const r of records) this.records.set(r.id, r);
  }

  async pullBlob(hash: string): Promise<Blob | undefined> {
    const b = this.blobs.get(hash);
    return b ? base64ToBlob(b.base64, b.mime) : undefined;
  }

  async pushBlob(hash: string, blob: Blob): Promise<void> {
    if (this.blobs.has(hash)) return; // 콘텐츠 주소화 — 같은 해시는 1벌만(결정 #18)
    this.blobs.set(hash, { mime: blob.type || "application/octet-stream", base64: await blobToBase64(blob) });
  }

  async hasBlob(hash: string): Promise<boolean> {
    return this.blobs.has(hash);
  }
}
